import type { AwardReference, AwardReview, AwardReviewKind, AwardReviewStatus } from '../types/awards';

/**
 * Wording for award pay review reminders, shared by the staff profile's Award
 * tab and /hr/award-reviews. The server decides what is due; this only says it.
 */

export const NOT_ADVICE =
  'These reminders are prompts to check, not advice. No reminder does not mean someone is classified or paid correctly: the award, and your own advice, decide that.';

export const KIND_TITLE: Record<AwardReviewKind, string> = {
  junior_birthday: 'Junior rate birthday',
  time_in_level: "Three months' experience",
  casual_conversion: 'Casual conversion',
  wage_review: 'Annual wage review',
  competency: 'New skill above their level',
  higher_duties: 'Higher duties',
};

/** The reminders a level change answers, and so offer one. */
export const RECLASSIFY_KINDS: AwardReviewKind[] = ['time_in_level', 'competency', 'higher_duties'];

export const SUBJECT_KIND_LABEL: Record<string, string> = {
  equipment_type: 'Kind of machine',
  equipment: 'One machine',
  activity_type: 'Type of work',
  certification: 'Certification',
  spray_operator: 'Operating a spray',
};

/** 150 → "2 h 30 min". */
export function minutesText(m: number): string {
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
}

export const STATUS_LABEL: Record<AwardReviewStatus, string> = {
  open: 'Open',
  reclassified: 'Reclassified',
  no_change: 'No change',
  dismissed: 'Dismissed',
};

export const STREAM_LABEL: Record<string, string> = {
  standard: 'Standard (junior rates by age)',
  apprentice: 'Apprentice',
  trainee: 'Trainee',
  supported_wage: 'Supported wage',
};

export const BASIS_LABEL: Record<string, string> = {
  full_time: 'Full time',
  part_time: 'Part time',
  casual: 'Casual',
};

export function awardName(ref: AwardReference | null, code: string | null | undefined): string {
  if (!code) return '';
  return ref?.awards.find(a => a.code === code)?.name || code;
}

export function levelLabel(ref: AwardReference | null, code: string | null | undefined, key: string | null | undefined): string {
  if (!key) return '';
  return ref?.awards.find(a => a.code === code)?.levels.find(l => l.key === key)?.label || key;
}

/** One sentence saying what changed and what to check. `formatDate` turns YYYY-MM-DD into display text. */
export function reviewSentence(
  r: AwardReview,
  ref: AwardReference | null,
  formatDate: (d: string) => string,
  today?: string,
): string {
  const d = r.detail || {};
  const who = r.name || 'They';
  const when = (verbPast: string, verbFuture: string) =>
    today && r.due_on < today ? `${verbPast} on ${formatDate(r.due_on)}` : today && r.due_on === today ? `${verbFuture} today` : `${verbFuture} on ${formatDate(r.due_on)}`;
  switch (r.kind) {
    case 'junior_birthday':
      return `${who} ${when(`turned ${d.age}`, `turns ${d.age}`)}. Under the ${awardName(ref, d.award_code)}, the junior rate moves from ${d.from_pct}% to ${d.to_pct}% of the adult rate for their level. Check their pay.`;
    case 'time_in_level': {
      const from = levelLabel(ref, d.award_code, d.from_level);
      const to = levelLabel(ref, d.award_code, d.to_level);
      const counted = d.counted_from_kind === 'industry_experience' ? 'their industry experience' : 'their start date';
      return `${who} ${when(`reached ${d.months} months`, `reaches ${d.months} months`)} counted from ${counted} (${d.counted_from ? formatDate(d.counted_from) : 'not set'}). ${from} moves to ${to} after ${d.months} months' industry experience. Review their classification.`;
    }
    case 'casual_conversion':
      return `${who} ${when(`reached ${d.months} months`, `reaches ${d.months} months`)} as a casual. They may ask to change to full or part time${d.small_business ? ' (12 months, as a small business employer)' : ''}. Check what the National Employment Standards ask of you.`;
    case 'competency': {
      const what = (d.items || []).map(i => i.label).join('; ');
      return `${who === 'They' ? 'They' : who} now ${who === 'They' ? 'hold' : 'holds'} what ${levelLabel(ref, d.award_code, d.highest_level)} lists under the ${awardName(ref, d.award_code)}${what ? ` (${what})` : ''}. Holding a skill is not the same as being required to use it: review whether their duties, and so their level, have changed.`;
    }
    case 'higher_duties': {
      const days = d.days || [];
      const whole = days.filter(x => x.whole_day_level).length;
      const first = days[0]?.date;
      return `${who} did duties mapped above their level on ${days.length} day${days.length === 1 ? '' : 's'}${first ? `, from ${formatDate(first)}` : ''}. More than 2 hours of a higher level's duties in a day is paid at that level for the whole day; 2 hours or less, for the time worked. Check their pay for these days.${d.suggest_reclassify ? ` It was more than 2 hours on ${whole} days: consider whether their level should change.` : ''}`;
    }
    case 'wage_review':
      return `Award rates change from the first full pay period on or after ${formatDate(r.due_on)}. Check the new rates for everyone on an award and update your payroll.`;
    default:
      return 'A pay review is due.';
  }
}
